import { useCallback, useEffect, useRef, useState } from 'react';

export const TICK_MS = 1000;

/**
 * A one-second countdown seeded from a server-measured number of seconds.
 *
 * Seconds, not a timestamp. Counting down to a parsed ISO deadline means
 * comparing the server's clock against the browser's, and a member whose
 * machine is a few minutes out would be shown a code lifetime wrong by the same
 * amount — the timestamp alone gives nothing to detect that with. The server
 * measures the remaining time and this counts it off, so only the elapsed
 * interval has to be right locally.
 *
 * Every read recomputes from the anchor rather than subtracting one, and
 * performance.now() is used rather than Date.now() because it is monotonic: a
 * clock change or a laptop waking from sleep cannot move the deadline.
 *
 * Recomputing on visibilitychange and focus is what makes it correct on return
 * to the tab. Browsers throttle setInterval in a hidden tab to roughly once a
 * minute, so the interval alone leaves the displayed number as much as a minute
 * stale, and it would then visibly snap when the next tick finally landed.
 *
 * @returns {Object} seconds remaining, plus onStart and onStop
 */
const useCountdown = () => {
    const [seconds, setSeconds] = useState(0);

    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    // Null means nothing is running, which is also what stops a wake-up from
    // resurrecting a countdown that has already finished or been stopped.
    const anchorRef = useRef<number | null>(null);
    const totalRef = useRef(0);

    const onStop = useCallback(() => {
        if (intervalRef.current) clearInterval(intervalRef.current);

        intervalRef.current = null;
        anchorRef.current = null;
    }, []);

    const onRecompute = useCallback(() => {
        if (anchorRef.current === null) return;

        const elapsed = (performance.now() - anchorRef.current) / 1000;
        const remaining = Math.max(Math.ceil(totalRef.current - elapsed), 0);

        setSeconds(remaining);

        if (remaining <= 0) onStop();
    }, [onStop]);

    /**
     * (Re)start the countdown. Always clears any interval already running —
     * useOtpTimer's onSetTimer does not, which is why a second resend there
     * counts down at double speed and orphans the first interval.
     *
     * @param {Number} [fromSeconds] server-measured seconds remaining
     * @returns {void}
     */
    const onStart = useCallback((fromSeconds?: number) => {
        onStop();

        const total = Math.max(Math.floor(fromSeconds || 0), 0);

        if (!total) {
            setSeconds(0);

            return;
        }

        totalRef.current = total;
        anchorRef.current = performance.now();

        onRecompute();

        intervalRef.current = setInterval(onRecompute, TICK_MS);
    }, [onRecompute, onStop]);

    // Attached once, for the life of the hook, rather than per countdown: the
    // handler is a no-op while nothing is running.
    useEffect(() => {
        const onWake = () => onRecompute();

        document.addEventListener('visibilitychange', onWake);
        window.addEventListener('focus', onWake);

        return () => {
            document.removeEventListener('visibilitychange', onWake);
            window.removeEventListener('focus', onWake);
        };
    }, [onRecompute]);

    useEffect(() => onStop, [onStop]);

    return { seconds, onStart, onStop };
};

export default useCountdown;
